import React, { useCallback, useEffect, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import * as Sentry from "@sentry/browser";
import {
  CssFontSource,
  CustomFontSource,
  Stripe,
  StripeElementsOptions,
} from "@stripe/stripe-js";
import { useConfig } from "./config";
import {
  Appearance,
  StripeElementLocale,
} from "@stripe/stripe-js/types/stripe-js/elements-group";
import { useI18n } from "../lib/i18n/i18n_provider";
import {
  classifyStripeJsFailure,
  loadStripeWithRetry,
  needsFreshDocumentToRetry,
  StripeJsFailure,
} from "./load_stripe_with_retry";
import { StripeUnavailable } from "./stripe_unavailable";

interface StripeProps {
  children?: React.ReactNode;
}

/**
 * Stands in for the client secret in the Elements key when there isn't one.
 *
 * Stripe's Elements group is immutable in the intent it was built from, so the
 * tree has to be keyed on the secret and a new intent genuinely needs a new
 * group. Exported because that keying is a contract with what the group holds,
 * not an implementation detail: anything reasoning about a secret-less tree —
 * or reproducing one — has to agree with this key or it is reasoning about a
 * remount that never happens.
 */
export const ELEMENTS_KEY_WITHOUT_SECRET = "stripe-elements-no-secret";

type StripeJsState =
  | { status: "loading" }
  // A load the reader asked for, tracked apart from the first one so the card
  // it was clicked from stays on screen instead of collapsing to nothing.
  | { status: "retrying" }
  // The cause is kept because it decides what retrying can even mean — see
  // `needsFreshDocumentToRetry`.
  | { status: "failed"; failure: StripeJsFailure }
  | { status: "loaded"; stripe: Stripe | null };

/**
 * Loads Stripe.js, keeping a failed load distinguishable from a pending one.
 *
 * Both states used to collapse into "no Stripe yet", which rendered nothing —
 * and because these providers gate whole surfaces, that turned a blocked
 * third-party script into a blank page with no explanation and no way forward.
 * The rejection also escaped as an unhandled promise, so the alert it raised
 * described a script error rather than the surface it had emptied.
 *
 * A failed load is not necessarily final: the one abandoned at the deadline is
 * still outstanding, and the state moves back to `loaded` if it answers.
 */
function useStripeJs(
  publishableKey: string,
  partnerSlug?: string,
  // Held off while the caller cannot yet say whether Stripe.js is even wanted.
  // A disabled load never leaves `loading`, which is what a caller that has not
  // asked a question should see.
  enabled = true
) {
  const [state, setState] = useState<StripeJsState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) {
      // A verdict outlives the question that asked it unless something clears
      // it. A checkout that turns out not to charge here after all — a promo
      // that takes it to zero, a request-to-book that collects nothing yet —
      // would otherwise stay refused by a script it no longer needs. Written as
      // a functional update so a caller that never enables the load does not
      // re-render into the state it is already in.
      setState((previous) =>
        previous.status === "loading" ? previous : { status: "loading" }
      );
      return;
    }

    let active = true;
    // A retry has already marked itself, synchronously with the click, so its
    // card and disabled button survive into the load. Every other start renders
    // nothing while pending.
    setState((previous) =>
      previous.status === "retrying" ? previous : { status: "loading" }
    );

    const markLoaded = (stripe: Stripe | null) => {
      if (active) {
        setState({ status: "loaded", stripe });
      }
    };

    // A load arriving late goes through the same door as one arriving on time.
    // The deadline stops the wait, not the request, so taking the late arrival
    // puts the payment surface back where it was; without it the card's own
    // button is the only way forward, and for a timeout that button is a
    // reload, which throws away whatever the reader had already filled in.
    loadStripeWithRetry(publishableKey, { onLateLoad: markLoaded })
      .then(markLoaded)
      .catch((error) => {
        if (!active) {
          return;
        }
        const failure = classifyStripeJsFailure(error);
        setState({ status: "failed", failure });
        reportStripeUnavailable(error, failure, partnerSlug);
      });

    return () => {
      active = false;
    };
    // `partnerSlug` only labels the report, so a slug that resolves later must
    // not restart a load that is already in flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishableKey, attempt, enabled]);

  // Marking the state here rather than deriving it from `attempt` disables the
  // button in the same commit as the click — a passive effect has not run yet —
  // and keeps the flag tied to one load rather than to a counter that never
  // resets, which would show the failure card over a later, healthy load.
  const retry = useCallback(() => {
    // Re-mounting the load would wait on the very request that timed out, so
    // the one recovery that can work is a new document.
    if (state.status === "failed" && needsFreshDocumentToRetry(state.failure)) {
      window.location.reload();
      return;
    }
    setState({ status: "retrying" });
    setAttempt((count) => count + 1);
  }, [state]);

  return { state, retrying: state.status === "retrying", retry };
}

/**
 * A blocked Stripe.js is a reader-environment problem no code change fixes, but
 * it stops a checkout dead, so the rate stays worth watching. Reported once per
 * exhausted load, under a stable fingerprint, rather than surfacing as one
 * unhandled rejection per injected script tag. Sentry records the url and
 * transaction itself; the partner is the dimension it cannot infer.
 *
 * Only a script that never arrived earns that treatment. The same rejection
 * carries an unset or malformed publishable key — Stripe's constructor throws on
 * one, and an unset `REACT_APP_STRIPE_PUBLISHABLE_KEY` leaves the config default
 * of `""` behind — which is a config regression that breaks every checkout for
 * the partner it hits. Demoting that to a warning inside the group nobody
 * triages would bury it, so it keeps its own message, level and grouping.
 */
function reportStripeUnavailable(
  error: unknown,
  failure: StripeJsFailure,
  partnerSlug?: string
): void {
  Sentry.withScope((scope) => {
    scope.setTag("stripe_js_unavailable", "true");
    scope.setTag("stripe_js_failure", failure);
    if (partnerSlug) {
      scope.setTag("partner_slug", partnerSlug);
    }
    if (failure !== "unexpected") {
      scope.setLevel("warning");
      scope.setFingerprint(["stripe-js-unavailable"]);
    }
    Sentry.captureException(error);
  });
}

/**
 * The key a Stripe surface will actually be built with.
 *
 * A partner on its own Stripe account supplies one through its payment config;
 * everyone else falls back to the platform key. Exported so the fallback is
 * resolved in one place: a caller that re-derives it is liable to reason about
 * a different Stripe account than the provider goes on to use, and nothing
 * would fail when the two drift apart.
 *
 * Empty until `/web_ui_config` answers. That is "not known yet", not a key, and
 * callers have to treat it as such.
 */
export function useEffectiveStripePublishableKey(partnerKey?: string): string {
  const config = useConfig();
  return partnerKey || config.stripeKey;
}

interface StripeJsAvailability {
  // Nothing can be charged with this Stripe.js: the load has failed, or a retry
  // of a failed one is still in flight. A first load still in flight is not yet
  // a refusal and never reads as one. A retry is the other way round — it
  // follows a failure already shown — so holding the refusal across it is what
  // keeps the reader from being handed back a button that still cannot pay.
  unusable: boolean;
  retrying: boolean;
  retry: () => void;
}

/**
 * Whether Stripe.js is usable, answered before a booking depends on it.
 *
 * Checkout only ever asked this from inside the payment modal, and that modal
 * opens after `POST /orders` has already created the order — so a booker whose
 * Stripe.js is blocked ended up with a booking on the books, a ten-second wait
 * and no way to pay for it. Asked from the page that collects the billing
 * details, the same verdict arrives while the form is still being filled, and
 * the checkout can refuse before it commits anything.
 *
 * The script itself is already in flight regardless — `@stripe/stripe-js`
 * injects it when the module is evaluated, long before any of this — so this
 * starts nothing the page was not already doing. What it moves earlier is the
 * *reading* of that load, which is the part that used to happen too late.
 *
 * `publishableKey` is undefined for a checkout that does not pay through
 * Stripe, and empty until config answers. Neither starts a load: `loadStripe("")`
 * rejects, which would report a config regression that has not happened and
 * clear Stripe's own script cache on the way. Both read as "nothing to refuse",
 * leaving the provider in the modal to report a genuinely unset key exactly as
 * it does today.
 */
export function useStripeJsAvailability(
  publishableKey?: string,
  // Carried through because this is now usually the first — and for a booker
  // who is stopped here, the only — report of a blocked script, and the partner
  // is the dimension Sentry cannot infer from the url.
  partnerSlug?: string
): StripeJsAvailability {
  const { state, retrying, retry } = useStripeJs(
    publishableKey ?? "",
    partnerSlug,
    !!publishableKey
  );

  return {
    unusable: state.status === "failed" || state.status === "retrying",
    retrying,
    retry,
  };
}

export function StripeProvider(props: StripeProps) {
  const { children } = props;
  const config = useConfig();
  const { state, retrying, retry } = useStripeJs(config.stripeKey);

  if (state.status === "failed" || retrying) {
    return <StripeUnavailable onRetry={retry} retrying={retrying} />;
  }

  if (state.status !== "loaded" || !state.stripe) {
    return null;
  }

  return <Elements stripe={state.stripe}>{children}</Elements>;
}

interface StripePropsV2 extends StripeProps {
  clientSecret?: string;
  partnerSlug?: string;
  stripePublishableKey?: string;
}

interface StripeProviderV2InnerProps {
  children?: React.ReactNode;
  clientSecret?: string;
  partnerSlug?: string;
  effectivePublishableKey: string;
}

const generateAppearance = (): Appearance => {
  const style = getComputedStyle(document.documentElement);
  const colorText = style.getPropertyValue("--bs-body-color");
  const colorDanger = style.getPropertyValue("--bs-danger");
  const fontFamily = style.getPropertyValue("--text-font-family");
  const textDefaultFontFamily = style.getPropertyValue("--text-default-font");
  const colorBackground =
    style.getPropertyValue("--stripe-color-background") || "#fff";

  return {
    theme: "stripe",
    variables: {
      colorText: colorText,
      fontFamily: textDefaultFontFamily || fontFamily,
      fontSmooth: "always",
      colorTextPlaceholder: "#aab7c4",
      colorDanger: colorDanger,
      colorBackground,
    },
    // In order to make label style consistent,
    // we copy css from class .form-label.text-form-field-v2__label in file form_field.css
    rules: {
      ".Label": {
        fontWeight: "600",
        fontSize: "1rem",
        lineHeight: "1.2",
        color: "#6a6678",
        textTransform: "uppercase",
        marginBottom: "0.625rem",
      },
    },
  };
};

// Fonts can live in the partner config or be exported by its theme file; prefer
// the config and fall back to the theme. Either source can be absent, so each
// load resolves to an array on failure — a partner with neither yields no custom
// fonts rather than throwing into Stripe Elements.
export async function resolvePartnerStripeFonts(
  loadConfig: () => Promise<{
    partnerConfig?: { stripeFonts?: (CssFontSource | CustomFontSource)[] };
  }>,
  loadTheme: () => Promise<{
    embedStripeFontFamilySrc?: (CssFontSource | CustomFontSource)[];
  }>
): Promise<(CssFontSource | CustomFontSource)[]> {
  const configFonts = await loadConfig()
    .then(({ partnerConfig }) => partnerConfig?.stripeFonts)
    .catch(() => undefined);
  if (configFonts) {
    return configFonts;
  }

  return loadTheme()
    .then((theme) => theme.embedStripeFontFamilySrc ?? [])
    .catch(() => []);
}

function StripeProviderV2Inner(props: StripeProviderV2InnerProps) {
  const { children, clientSecret, partnerSlug, effectivePublishableKey } =
    props;
  const { locale } = useI18n();
  const [fontSource, setFontSource] = useState<
    (CssFontSource | CustomFontSource)[]
  >([]);

  useEffect(() => {
    if (!partnerSlug) {
      return;
    }
    // Guard against a slow load resolving after partnerSlug has already changed
    // and overwriting the newer partner's fonts.
    let active = true;
    resolvePartnerStripeFonts(
      () =>
        import(`../offering/${partnerSlug}/${partnerSlug}_partner_config.tsx`),
      () => import(`../offering/${partnerSlug}/${partnerSlug}_theme.tsx`)
    ).then((fonts) => {
      if (active) {
        setFontSource(fonts);
      }
    });
    return () => {
      active = false;
    };
  }, [partnerSlug]);

  const { state, retrying, retry } = useStripeJs(
    effectivePublishableKey,
    partnerSlug
  );

  // Ahead of building the Elements options, which reads computed styles off the
  // document and would be discarded on either of these paths.
  if (state.status === "failed" || retrying) {
    return <StripeUnavailable onRetry={retry} retrying={retrying} />;
  }

  if (state.status !== "loaded" || !state.stripe) {
    return null;
  }

  // An empty secret is no secret. It reaches here as an ordinary absence — a
  // caller passing through a field its own type promised was a string — and
  // handing it to Stripe would build a group around an intent that cannot be
  // looked up, while keying on it would call two such groups the same one.
  const intentSecret = clientSecret || undefined;

  const options: StripeElementsOptions = {
    appearance: generateAppearance(),
    clientSecret: intentSecret,
    locale: locale.baseLanguageTag as StripeElementLocale,
    fonts: fontSource,
  };

  return (
    <Elements
      key={intentSecret ?? ELEMENTS_KEY_WITHOUT_SECRET}
      stripe={state.stripe}
      options={options}
    >
      {children}
    </Elements>
  );
}

export function StripeProviderV2(props: StripePropsV2) {
  const { children, clientSecret, stripePublishableKey, partnerSlug } = props;
  const effectivePublishableKey =
    useEffectiveStripePublishableKey(stripePublishableKey);

  return (
    <StripeProviderV2Inner
      key={effectivePublishableKey}
      clientSecret={clientSecret}
      effectivePublishableKey={effectivePublishableKey}
      partnerSlug={partnerSlug}
    >
      {children}
    </StripeProviderV2Inner>
  );
}
